import { useEffect, useState } from "react";
import { Box, CircularProgress, Link, Stack } from "@mui/material";
import { TobBar } from "src/shared/components/top-bar";
import { useTranslation } from "react-i18next";
import {
  useGetCarRequestTransaction,
  usePayMutation,
  useVerifyRedirectedPaymentMutation,
} from "src/api/car-request";
import RequestCarWash from "./components/steps/request-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import FailedTicket from "./components/failed-ticket";
import CheckedOutTicket from "./components/cancelled-request";
import PaymentType from "./components/steps/payment-type";
import { LoadingButton } from "@mui/lab";
import { defaultSteps, schema, FormData } from "./constants";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CancelledSuccessfully from "./components/steps/cancelled";
import PaymentFailed from "./components/payment-failed";
import PolicyPopup from "src/shared/components/policy-popup";
import CarWashType from "./components/steps/car-wash-type";
import {
  useCancelCarWashRequestMutation,
  useCreateCarWashRequestMutation,
  useGetLocationCarWashServices,
} from "src/api/car-wash";
import RequestedSuccessfully from "../car-request/components/steps/requested-successfully";
import { IAttachmentFile } from "src/shared/components/file-uploader";
import { uploadCarWashReqAttachment } from "src/utils/_firbaseStorage";
import { ICarBrand, ICarColor } from "src/types/generic";
import { collection, getDocs } from "firebase/firestore";
import { db } from "src/firebase";
import { useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import sarIcon from "src/assets/svg-icons/sar.svg";

const CarWashRequestPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  const redirectedPaymentSessionId = searchParams.get("cko-session-id");
  const [steps] = useState(defaultSteps);
  const [currentStep, setCurrentStep] = useState(0);
  const [applePaySession, setApplePaySession] = useState<any>();
  const [payStatus, setPayStatus] = useState<
    "free" | "paid" | "notPaid" | undefined
  >();
  const [open, setOpen] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(false);
  const [files, setFiles] = useState<IAttachmentFile[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [carBrands, setCarBrands] = useState<ICarBrand[]>([]);
  const [carColors, setCarColors] = useState<ICarColor[]>([]);
  const setNotifications = useSetRecoilState(notificationsState);

  // APIS
  const { data, error, status, refetch, isFetching } =
    useGetCarRequestTransaction(params.id ?? "");
  const {
    data: locationData,
    error: locationDataError,
    status: locationDataStatus,
    refetch: locationDataRefetch,
    isFetching: locationDataIsFetching,
  } = useGetLocationCarWashServices(params.locationId ?? "");
  const {
    mutate,
    status: createReqStatus,
    isPending,
    data: createReqResponse,
  } = useCreateCarWashRequestMutation();
  const {
    mutate: mutateCancelReq,
    status: cancelReqStatus,
    isPending: isPendingCancel,
    error: cancelReqError,
  } = useCancelCarWashRequestMutation();
  const {
    mutate: mutatePay,
    isPending: isPendingPay,
    status: statusPay,
    error: errorPay,
    data: payResponse,
  } = usePayMutation();
  const {
    mutate: mutateVerifyPay,
    isPending: isPendingVerifyPay,
    status: statusVerifyPay,
  } = useVerifyRedirectedPaymentMutation();

  // React hook form
  const methods = useForm<FormData>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      plateType: "saudi",
      countryCode: "+966",
      isoCode: "SA",
    },
  });
  const { trigger, getValues, watch } = methods;

  const isFreeTicket = data?.data.price === 0;

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenPolicy = () => {
    setOpenPolicy(true);
  };

  const handleClosePolicy = () => {
    setOpenPolicy(false);
  };

  const isPaymentStep = currentStep === 2 && payStatus === "notPaid";

  const isPartialPaid =
    !!data &&
    data.data.paymentInfo?.amount > 0 &&
    data.data.paymentInfo?.unpaidAmount > 0;

  // Validate Steps
  const validate = async () => {
    let stepFields = steps[currentStep].fields;
    const validStep = await trigger(stepFields as any[], { shouldFocus: true });
    return validStep;
  };

  const handleCarWashRequest = async () => {
    setLoading(true);
    const uploadedAttachments = await Promise.all(
      files.map((attachment) =>
        uploadCarWashReqAttachment(`${params.locationId}`, attachment.file!),
      ),
    );

    const data = getValues();
    const selectedService = locationData?.data?.services?.find(
      (service: { name: string }) => service.name === data.serviceName,
    );

    const selectedBrand = carBrands?.find(
      (brand) => brand.id === data.carBrand,
    );
    const selectedColor = carColors?.find(
      (color) => color.id === data.carColor,
    );

    mutate({
      locationId: params.locationId ?? "",
      customerName:  data.userName,
      variant: {
        type: selectedService?.type,
        name: selectedService?.name ?? data.serviceName,
      },
      plateLetter: data.plateLetters,
      plateNumber: data.plateType === "saudi" ? (data.plateNum ?? "") : "",
      isSaudi: data.plateType === "saudi",
      customerNumber: {
        countryCode: data.countryCode,
        isoCode: data.isoCode,
        mobile: data.userPhone,
      },
      images: uploadedAttachments.map((attachment) => attachment.url),
      carInfo: {
        brandId: selectedBrand?.id ?? "",
        brandNameAr: selectedBrand?.nameAr ?? "",
        brandNameEn: selectedBrand?.nameEn ?? "",
        colorId: selectedColor?.id ?? "",
        colorNameAr: selectedColor?.nameAr ?? "",
        colorNameEn: selectedColor?.nameEn ?? "",
        description: null,
      },
    });
  };

  const goNext = async () => {
    const valid = await validate();
    if (!valid) return;
    if (currentStep <= steps.length - 1) {
      // request details step
      if (currentStep === 1) {
        handleCarWashRequest();
        return;
      }
      // Payment step
      if (currentStep === 2 && payStatus === "notPaid") {
        if (getValues("paymentType") === "cashier") {
          setCurrentStep((prev) => prev + 1);
        } else if (getValues("paymentType") === "card") {
          const expiryArr = getValues("expiry_at")?.split("/") || [];
          const month = expiryArr[0];
          const year = expiryArr[1];
          mutatePay({
            ticketId: params.id as string,
            type: "card",
            number: getValues("cardNumber") as string,
            expiry_month: month,
            expiry_year: Number(year),
            name: getValues("name") as string,
            cvv: Number(getValues("cvv")),
          });
        } else if (getValues("paymentType") === "amex") {
          const expiryArr = getValues("amexExpiryAt")?.split("/") || [];
          const month = expiryArr[0];
          const year = expiryArr[1];
          mutatePay({
            ticketId: params.id as string,
            type: "amex",
            number: getValues("amexCardNumber") as string,
            expiry_month: month,
            expiry_year: Number(year),
            name: getValues("amexName") as string,
            cvv: Number(getValues("amexCvv")),
          });
        }
        return;
      }
      // Requested successfully step. Next step: cancellation
      // if (currentStep === steps.length - 2) {
      //   mutateCancelReq({
      //     ticketId: params.id as string,
      //   });
      //   return;
      // }
      // Cancelled step. Next step: date and time to re-request
      if (currentStep === steps.length - 1) {
        setCurrentStep(0);
        return;
      }
      setCurrentStep((prev) => prev + 1);
    }
  };

  // When we have redirected payment session Id (3D Secure cases)
  useEffect(() => {
    if (redirectedPaymentSessionId) {
      mutateVerifyPay({
        ticketId: params.id as string,
        sessionId: redirectedPaymentSessionId,
      });
    }
  }, []);

  // Get car request data success handling
  useEffect(() => {
    if (status === "success") {
      setLoading(false);

      if (isFreeTicket) {
        setPayStatus("free");
        setCurrentStep(3);
      } else if (
        !!data?.data.paymentInfo &&
        data?.data.paymentInfo.unpaidAmount <= 0
      ) {
        setPayStatus("paid");
        setCurrentStep(3);
      } else {
        setPayStatus("notPaid");
        if (locationData?.data?.isOnlinePaymentsEnabled) {
          setCurrentStep(2);
        } else {
          setCurrentStep(3);
        }
      }
    }
  }, [status, isFetching]);

  const onPaymentSuccess = () => {
    refetch();
  };

  //Create Req Success handling
  useEffect(() => {
    if (createReqStatus === "success") {
      navigate(
        `/car-wash/${params.locationId}/${createReqResponse?.data?.ticketId}`,
        { replace: true },
      );
      const requestedStepIndex = steps.findIndex(
        (step) => step.id === "paymentType",
      );
      setCurrentStep(requestedStepIndex);
    }
  }, [createReqStatus]);

  //Cancel Req Success handling
  useEffect(() => {
    if (cancelReqStatus === "success") {
      setCurrentStep((prev) => prev + 1);
      navigate(`/car-wash/${params.locationId}`, { replace: true });
    } else if (cancelReqStatus === "error") {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: cancelReqError?.response?.data?.error ?? t("somethingWrong"),
        },
      ]);
    }
  }, [cancelReqStatus]);

  //Pay success and error handling
  useEffect(() => {
    if (statusPay === "success") {
      onPaymentSuccess();
      // apple pay success
      if (watch("paymentType") === "applepay") {
        console.log("apple pay succeeded");
        const windowCopy: any = window;
        applePaySession?.completePayment(
          windowCopy?.ApplePaySession?.STATUS_SUCCESS,
        );
      }
    } else if (statusPay === "error") {
      // apple pay failure
      if (watch("paymentType") === "applepay") {
        console.log("apple pay failed", { payResponse, errorPay });
        const windowCopy: any = window;
        applePaySession?.completePayment(
          windowCopy?.ApplePaySession?.STATUS_FAILURE,
        );
      }
      // Type of card that needs redirection
      const errorData = errorPay?.response?.data?.data;
      if (errorData?.status === "Pending" && !!errorData?.redirectLink) {
        window.location.href = errorData?.redirectLink;
        return;
      }
      setOpen(true);
    }
  }, [statusPay]);

  //Verify payment req success handling (after redirection)
  useEffect(() => {
    if (statusVerifyPay === "success") {
      // setValue("time", searchParams.get("time") as string);
      // setValue("gate", searchParams.get("gate") as string);
      onPaymentSuccess();
      setSearchParams();
    } else if (statusVerifyPay === "error") {
      // setValue("time", searchParams.get("time") as string);
      // setValue("gate", searchParams.get("gate") as string);
      const paymentStepIndex = steps.findIndex(
        (step) => step.id === "paymentType",
      );
      // To make sure that it's still unpaid
      if (paymentStepIndex !== -1) {
        setOpen(true);
        setCurrentStep(paymentStepIndex);
      }
      setSearchParams();
    }
  }, [statusVerifyPay]);

  // Refresh Page Every 5 Minutes (300,000 ms)
  useEffect(() => {
    const interval = setInterval(() => {
      window.location.reload();
    }, 300000); // 5 minutes = 300,000 ms

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // fetch car colors
  useEffect(() => {
    const fetchCarBrands = async () => {
      const snapshot = await getDocs(collection(db, "car_brands"));

      setCarBrands(
        snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    };

    fetchCarBrands();
  }, []);

  // fetch car colors
  useEffect(() => {
    const fetchCarColors = async () => {
      const snapshot = await getDocs(collection(db, "car_colors"));

      setCarColors(
        snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    };

    fetchCarColors();
  }, []);

  return (
    <Box display={"flex"} justifyContent={"center"} minHeight={"95vh"}>
      <Box
        boxShadow={{ xs: "unset", sm: "0px 0px 10px rgba(255, 255, 255, 0.3)" }}
        borderRadius={"50px"}
        minWidth={{ xs: "100%", sm: "375px" }}
        maxWidth={{ xs: "100%", sm: "375px" }}
        bgcolor={"#0E0E0E"}
        px={"33px"}
        pb={"48px"}
        display={"flex"}
        flexDirection={"column"}
      >
        <TobBar />

        {isFetching ||
        isPendingVerifyPay ||
        isPending ||
        locationDataIsFetching ||
        loading ? (
          <Box pt={"200px"} display={"flex"} justifyContent={"center"}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error?.response.status ||
            locationDataError?.response?.status === 404 ? (
              <FailedTicket />
            ) : (
              (!params?.id || data) && (
                <>
                  {data?.data?.status === "cancelled" ||
                  data?.data?.status === "closed" ? (
                    <CheckedOutTicket
                      title={
                        data?.data?.status === "cancelled"
                          ? "requestIsCancelled"
                          : "requestIsClosed"
                      }
                    />
                  ) : (
                    <>
                      {/* Steps */}
                      <FormProvider {...methods}>
                        {currentStep === 0 && (
                          <CarWashType
                            types={locationData?.data?.services?.map(
                              (service: any) => {
                                return {
                                  label: (
                                    <>
                                      {service.name} (
                                      <img
                                        src={sarIcon}
                                        alt="SAR"
                                        width={11}
                                        height={11}
                                      />{" "}
                                      {service.fixedPrice})
                                    </>
                                  ),
                                  value: service.name,
                                };
                              },
                            )}
                          />
                        )}
                        {currentStep === 1 && (
                          <RequestCarWash
                            files={files}
                            setFiles={setFiles}
                            carBrands={carBrands}
                            setCarBrands={setCarBrands}
                            carColors={carColors}
                            setCarColors={setCarColors}
                          />
                        )}
                        {payStatus === "notPaid" && currentStep === 2 && (
                          <PaymentType
                            price={data?.data?.paymentInfo?.unpaidAmount || 0}
                            mutatePay={mutatePay}
                            setApplePaySession={setApplePaySession}
                            isPartialPaid={isPartialPaid}
                            amexDiscountValue={0}
                            isOnlinePaymentsEnabled={
                              locationData?.data?.isOnlinePaymentsEnabled
                            }
                            hasGlobalDiscount={false}
                            globalDiscountPer={0}
                          />
                        )}
                        {currentStep === steps.length - 2 && data && (
                          <RequestedSuccessfully
                            data={data?.data}
                            setCurrentStep={setCurrentStep}
                            hasDescription={false}
                          />
                        )}
                        {currentStep === steps.length - 1 && data && (
                          <CancelledSuccessfully data={data?.data} />
                        )}
                      </FormProvider>

                      {/* Stepper */}
                      <Stack flexGrow={1}>
                        <Box mt={"auto"}>
                          {currentStep === 0 && (
                            <Stack gap={2} alignItems={"center"}>
                              <Link
                                onClick={handleOpenPolicy}
                                sx={{ cursor: "pointer" }}
                              >
                                {t("arsannUsagePolicy")}
                              </Link>
                            </Stack>
                          )}
                          {!(
                            isPaymentStep &&
                            (watch("paymentType") === "applepay" ||
                              watch("paymentType") === "googlepay")
                          ) &&
                            currentStep !== steps.length - 2 && (
                              <LoadingButton
                                onClick={goNext}
                                fullWidth
                                variant={
                                  currentStep === steps.length - 2
                                    ? "outlined"
                                    : "contained"
                                }
                                color={
                                  currentStep === steps.length - 2
                                    ? "error"
                                    : "primary"
                                }
                                size="large"
                                loading={
                                  isPending ||
                                  isPendingCancel ||
                                  isPendingPay ||
                                  loading
                                }
                                sx={{
                                  color: "#FFFDF8",
                                  mt: 2,
                                  textTransform: "uppercase",
                                }}
                              >
                                {currentStep === 1
                                  ? t("requestCarWash")
                                  : currentStep === steps.length - 2
                                    ? t("cancelReq")
                                    : t("continue")}
                              </LoadingButton>
                            )}
                        </Box>
                      </Stack>
                    </>
                  )}
                </>
              )
            )}
          </>
        )}
      </Box>
      <PaymentFailed open={open} handleClose={handleClose} />
      <PolicyPopup open={openPolicy} handleClose={handleClosePolicy} />
    </Box>
  );
};

export default CarWashRequestPage;
